import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import { fetchCustomerOrders, type CustomerOrder } from '../lib/customerOrders';
import type { CrmRequiredActionItem } from '../lib/crm/requiredActions';
import { createBusinessCache } from '../lib/controlCentre/actionsCache';

const ordersCache = createBusinessCache<CustomerOrder[]>({
  persistKey: 'order-approvals',
});

function buildOrderApprovalActions(orders: CustomerOrder[]): CrmRequiredActionItem[] {
  return orders.map((order) => {
    const placedAt = order.placed_at || order.created_at;
    const placedMs = new Date(placedAt).getTime();
    const total = (order.estimated_total_cents || 0) / 100;
    const totalLabel = `$${total.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
    return {
      kind: 'order_approval' as const,
      orderId: order.id,
      title: order.contact_name?.trim() || 'Guest customer',
      customerName: `#${order.order_number}`,
      subtitle: `${totalLabel} · placed ${formatDistanceToNow(new Date(placedAt), { addSuffix: true })}`,
      sortOrder: Number.isNaN(placedMs) ? 0 : -placedMs,
      createdAt: placedAt,
    };
  });
}

/** Customer orders sitting in "new" — placed (usually online) and waiting for approval. */
export function useOrderRequiredActions(enabled = true) {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id ?? '';

  const [orders, setOrders] = useState<CustomerOrder[]>(
    () => ordersCache.get(businessId) ?? []
  );
  const [isLoading, setIsLoading] = useState(() => !ordersCache.has(businessId));
  const [error, setError] = useState<string | null>(null);

  const fetchPending = useCallback(async () => {
    if (!businessId) return;
    const pending = await fetchCustomerOrders(businessId, { status: 'new' });
    setOrders(pending);
    ordersCache.set(businessId, pending);
  }, [businessId]);

  const load = useCallback(async () => {
    if (!enabled || !businessId || !supabase) {
      setOrders([]);
      setIsLoading(false);
      return;
    }

    const hasCache = ordersCache.has(businessId);
    if (hasCache) {
      setOrders(ordersCache.get(businessId) ?? []);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
    setError(null);
    try {
      await fetchPending();
    } catch (err) {
      console.error('Error loading order approval actions:', err);
      setError('Failed to load pending orders.');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, businessId, supabase, fetchPending]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      setOrders([]);
      return;
    }
    void load();
  }, [load, enabled]);

  // Live: new online orders should appear without a page refresh.
  const refreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!enabled || !businessId || !supabase || isLoading) return;

    const channel = supabase
      .channel(`order-approval-actions-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'customer_orders',
          filter: `business_id=eq.${businessId}`,
        },
        () => {
          if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
          refreshDebounceRef.current = setTimeout(() => {
            void fetchPending();
          }, 400);
        }
      )
      .subscribe();

    return () => {
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
      void supabase.removeChannel(channel);
    };
  }, [enabled, businessId, supabase, isLoading, fetchPending]);

  const orderActions = useMemo(() => buildOrderApprovalActions(orders), [orders]);

  return {
    pendingOrders: orders,
    orderActions,
    isLoading,
    error,
    refresh: load,
  };
}
